import { Component } from '@theme/component';
import { morphSection } from '@theme/section-renderer';
import { DiscountUpdateEvent } from '@theme/events';
import { fetchConfig } from '@theme/utilities';
import { cartPerformance } from '@theme/performance';

/**
 * A custom element that applies a discount to the cart.
 *
 * @typedef {Object} CartDiscountComponentRefs
 * @property {HTMLElement} cartDiscountError - The error element.
 * @property {HTMLElement} cartDiscountErrorDiscountCode - The discount code error element.
 * @property {HTMLElement} cartDiscountErrorShipping - The shipping error element.
 */

/**
 * @extends {Component<CartDiscountComponentRefs>}
 */
class CartDiscount extends Component {
  requiredRefs = ['cartDiscountError', 'cartDiscountErrorDiscountCode', 'cartDiscountErrorShipping'];

  /** @type {AbortController | null} */
  #activeFetch = null;


  #createAbortController() {
    if (this.#activeFetch) {
      this.#activeFetch.abort();
    }

    const abortController = new AbortController();
    this.#activeFetch = abortController;
    return abortController;
  }

  /**
   * Handles updates to the cart note.
   * @param {SubmitEvent} event - The submit event on our form.
   */
  applyDiscount = async (event) => {
    const { cartDiscountError, cartDiscountErrorDiscountCode, cartDiscountErrorShipping } = this.refs;

    event.preventDefault();
    event.stopPropagation();

    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;

    const discountCode = form.querySelector('input[name="discount"]');
    if (!(discountCode instanceof HTMLInputElement) || typeof this.dataset.sectionId !== 'string') return;

    const discountCodeValue = discountCode.value;

    const abortController = this.#createAbortController();

    try {
      const existingDiscounts = this.#existingDiscounts();
      if (existingDiscounts.includes(discountCodeValue)) return;

      cartDiscountError.classList.add('hidden');
      cartDiscountErrorDiscountCode.classList.add('hidden');
      cartDiscountErrorShipping.classList.add('hidden');

      const config = fetchConfig('json', {
        body: JSON.stringify({
          discount: [...existingDiscounts, discountCodeValue].join(','),
          sections: [this.dataset.sectionId],
        }),
      });

      const response = await fetch(Theme.routes.cart_update_url, {
        ...config,
        signal: abortController.signal,
      });

      const data = await response.json();

      if (
        data.discount_codes.find((/** @type {{ code: string; applicable: boolean; }} */ discount) => {
          return discount.code === discountCodeValue && discount.applicable === false;
        })
      ) {
        discountCode.value = '';
        this.#handleDiscountError('discount_code');
        return;
      }

      const newHtml = data.sections[this.dataset.sectionId];
      const parsedHtml = new DOMParser().parseFromString(newHtml, 'text/html');
      const section = parsedHtml.getElementById(`shopify-section-${this.dataset.sectionId}`);
      const discountCodes = section?.querySelectorAll('.cart-discount__pill') || [];
      if (section) {
        const codes = Array.from(discountCodes)
          .map((element) => (element instanceof HTMLLIElement ? element.dataset.discountCode : null))
          .filter(Boolean);
        // Before morphing, we need to check if the shipping discount is applicable in the UI
        // we check the liquid logic compared to the cart payload to assess whether we leveraged
        // a valid shipping discount code.
        if (
          codes.length === existingDiscounts.length &&
          codes.every((/** @type {string} */ code) => existingDiscounts.includes(code)) &&
          data.discount_codes.find((/** @type {{ code: string; applicable: boolean; }} */ discount) => {
            return discount.code === discountCodeValue && discount.applicable === true;
          })
        ) {
          this.#handleDiscountError('shipping');
          discountCode.value = '';
          return;
        }
      }

      document.dispatchEvent(new DiscountUpdateEvent(data, this.id));
      morphSection(this.dataset.sectionId, newHtml);
    } catch (error) {
      // Ignore abort errors (they're expected when cancelling previous requests)
      if (error.name !== 'AbortError') {
        console.error('Error applying discount:', error);
      }
    } finally {
      this.#activeFetch = null;
      cartPerformance.measureFromEvent('discount-update:user-action', event);
    }
  };

  /**
   * Handles applying a discount from a clickable coupon button.
   * @param {MouseEvent | KeyboardEvent} event - The mouse or keyboard event on the clickable button.
   */
  applyClickableDiscount = async (event) => {
    event.preventDefault();
    event.stopPropagation();

    if (
      (event instanceof KeyboardEvent && event.key !== 'Enter') ||
      !(event instanceof MouseEvent) ||
      !(event.target instanceof HTMLElement) ||
      typeof this.dataset.sectionId !== 'string'
    ) {
      return;
    }

    const button = event.target.closest('.cart-discount__clickable-button');
    if (!(button instanceof HTMLButtonElement)) return;

    const discountCode = button.dataset.couponCode;
    if (!discountCode || button.disabled) return;

    const { cartDiscountError, cartDiscountErrorDiscountCode, cartDiscountErrorShipping } = this.refs;
    const existingDiscounts = this.#existingDiscounts();

    // Check if already applied
    if (existingDiscounts.includes(discountCode)) {
      return;
    }

    const abortController = this.#createAbortController();

    try {
      cartDiscountError.classList.add('hidden');
      cartDiscountErrorDiscountCode.classList.add('hidden');
      cartDiscountErrorShipping.classList.add('hidden');

      const config = fetchConfig('json', {
        body: JSON.stringify({
          discount: [...existingDiscounts, discountCode].join(','),
          sections: [this.dataset.sectionId],
        }),
      });

      const response = await fetch(Theme.routes.cart_update_url, {
        ...config,
        signal: abortController.signal,
      });

      const data = await response.json();

      if (
        data.discount_codes.find((/** @type {{ code: string; applicable: boolean; }} */ discount) => {
          return discount.code === discountCode && discount.applicable === false;
        })
      ) {
        this.#handleDiscountError('discount_code');
        return;
      }

      const newHtml = data.sections[this.dataset.sectionId];
      const parsedHtml = new DOMParser().parseFromString(newHtml, 'text/html');
      const section = parsedHtml.getElementById(`shopify-section-${this.dataset.sectionId}`);
      const discountCodes = section?.querySelectorAll('.cart-discount__pill') || [];
      if (section) {
        const codes = Array.from(discountCodes)
          .map((element) => (element instanceof HTMLLIElement ? element.dataset.discountCode : null))
          .filter(Boolean);
        // Before morphing, we need to check if the shipping discount is applicable in the UI
        // we check the liquid logic compared to the cart payload to assess whether we leveraged
        // a valid shipping discount code.
        if (
          codes.length === existingDiscounts.length &&
          codes.every((/** @type {string} */ code) => existingDiscounts.includes(code)) &&
          data.discount_codes.find((/** @type {{ code: string; applicable: boolean; }} */ discount) => {
            return discount.code === discountCode && discount.applicable === true;
          })
        ) {
          this.#handleDiscountError('shipping');
          return;
        }
      }

      // Morph immediately with the response HTML for instant UI feedback
      // The response HTML contains the updated cart total, so this should update everything immediately
      morphSection(this.dataset.sectionId, newHtml);

      // Dispatch event to ensure any other components listening are notified
      // This happens synchronously after morph to ensure consistency
      document.dispatchEvent(new DiscountUpdateEvent(data, this.id));
    } catch (error) {
      // Ignore abort errors (they're expected when cancelling previous requests)
      if (error.name !== 'AbortError') {
        console.error('Error applying clickable discount:', error);
      }
    } finally {
      this.#activeFetch = null;
      cartPerformance.measureFromEvent('discount-update:user-action', event);
    }
  };

  /**
   * Handles removing a discount from the cart.
   * @param {MouseEvent | KeyboardEvent} event - The mouse or keyboard event in our pill.
   */
  removeDiscount = async (event) => {
    event.preventDefault();
    event.stopPropagation();

    if (
      (event instanceof KeyboardEvent && event.key !== 'Enter') ||
      !(event instanceof MouseEvent) ||
      !(event.target instanceof HTMLElement) ||
      typeof this.dataset.sectionId !== 'string'
    ) {
      return;
    }

    // Check for pill first (original structure)
    const pill = event.target.closest('.cart-discount__pill');
    let discountCode;

    if (pill instanceof HTMLLIElement) {
      discountCode = pill.dataset.discountCode;
    } else {
      // Check for offer card remove button
      const offerButton = event.target.closest('.cart-discount__offer-remove-button');
      if (offerButton instanceof HTMLButtonElement && offerButton.dataset.discountCode) {
        discountCode = offerButton.dataset.discountCode;
      } else {
        return;
      }
    }

    if (!discountCode) return;

    // Get all existing discount codes from pills (original case for API)
    /** @type {string[]} */
    const existingDiscounts = [];
    const allPills = this.querySelectorAll('.cart-discount__pill[data-discount-code]');

    for (const p of allPills) {
      if (p instanceof HTMLLIElement && p.dataset.discountCode) {
        const code = p.dataset.discountCode;
        if (code) {
          existingDiscounts.push(code);
        }
      }
    }

    // Find and remove the code (case-insensitive comparison)
    const codeToRemoveUpper = discountCode.toUpperCase().trim();
    let indexToRemove = -1;

    for (let i = 0; i < existingDiscounts.length; i++) {
      const code = existingDiscounts[i];
      if (code && code.toUpperCase().trim() === codeToRemoveUpper) {
        indexToRemove = i;
        break;
      }
    }

    if (indexToRemove === -1) return;

    existingDiscounts.splice(indexToRemove, 1);

    const abortController = this.#createAbortController();

    try {
      // Convert to uppercase to match API format (same as applyClickableDiscount)
      const discountValue = existingDiscounts.length > 0
        ? existingDiscounts.map(code => code.toUpperCase().trim()).join(',')
        : '';

      const config = fetchConfig('json', {
        body: JSON.stringify({ discount: discountValue, sections: [this.dataset.sectionId] }),
      });

      const response = await fetch(Theme.routes.cart_update_url, {
        ...config,
        signal: abortController.signal,
      });

      const data = await response.json();

      document.dispatchEvent(new DiscountUpdateEvent(data, this.id));
      morphSection(this.dataset.sectionId, data.sections[this.dataset.sectionId]);
    } catch (error) {
      // Ignore abort errors (they're expected when cancelling previous requests)
      if (error.name !== 'AbortError') {
        console.error('Error removing discount:', error);
      }
    } finally {
      this.#activeFetch = null;
    }
  };

  /**
   * Handles the discount error.
   *
   * @param {'discount_code' | 'shipping'} type - The type of discount error.
   */
  #handleDiscountError(type) {
    const { cartDiscountError, cartDiscountErrorDiscountCode, cartDiscountErrorShipping } = this.refs;
    const target = type === 'discount_code' ? cartDiscountErrorDiscountCode : cartDiscountErrorShipping;
    cartDiscountError.classList.remove('hidden');
    target.classList.remove('hidden');
  }

  /**
   * Handles removing a discount from the offer card.
   * @param {MouseEvent | KeyboardEvent} event - The mouse or keyboard event on the remove button.
   */
  removeDiscountFromOffer = async (event) => {
    console.log('removeDiscountFromOffer called', event);
    event.preventDefault();
    event.stopPropagation();

    if (
      (event instanceof KeyboardEvent && event.key !== 'Enter') ||
      !(event instanceof MouseEvent) ||
      !(event.target instanceof HTMLElement) ||
      typeof this.dataset.sectionId !== 'string'
    ) {
      console.log('Early return:', {
        isKeyboard: event instanceof KeyboardEvent,
        isMouse: event instanceof MouseEvent,
        hasTarget: event.target instanceof HTMLElement,
        hasSectionId: typeof this.dataset.sectionId === 'string'
      });
      return;
    }

    // Find the button - handle clicks on SVG elements inside the button
    const button = event.target.closest('.cart-discount__offer-remove-button');
    if (!(button instanceof HTMLButtonElement)) {
      console.log('Button not found, target:', event.target, 'closest result:', button);
      return;
    }

    const discountCode = button.dataset.discountCode;
    if (!discountCode) {
      console.log('No discount code found on button', button.dataset);
      return;
    }

    console.log('Removing discount code:', discountCode);

    // Use the same method as applyClickableDiscount - gets all codes in uppercase format
    // This ensures consistency with how codes are stored and sent to the API
    const existingDiscounts = this.#existingDiscounts();
    console.log('Existing discounts:', existingDiscounts);

    // The code to remove (already uppercase from template)
    const codeToRemoveUpper = discountCode.toUpperCase().trim();

    // Find and remove the code (case-insensitive, but codes are already uppercase)
    const remainingDiscounts = existingDiscounts.filter(code => {
      return code && code.toUpperCase().trim() !== codeToRemoveUpper;
    });

    console.log('Remaining discounts after removal:', remainingDiscounts);

    // Use the filtered list
    const finalDiscounts = remainingDiscounts;

    const abortController = this.#createAbortController();

    try {
      // Send the remaining discount codes (or empty string if none)
      // Codes are already uppercase from #existingDiscounts()
      const discountValue = finalDiscounts.length > 0
        ? finalDiscounts.join(',')
        : '';

      console.log('Sending discount value to API:', discountValue);

      const config = fetchConfig('json', {
        body: JSON.stringify({ discount: discountValue, sections: [this.dataset.sectionId] }),
      });

      const response = await fetch(Theme.routes.cart_update_url, {
        ...config,
        signal: abortController.signal,
      });

      if (!response.ok) {
        throw new Error(`Failed to update cart: ${response.status}`);
      }

      const data = await response.json();
      console.log('Cart update response:', data);

      if (!data.sections || !data.sections[this.dataset.sectionId]) {
        throw new Error('Invalid response from server');
      }

      document.dispatchEvent(new DiscountUpdateEvent(data, this.id));
      morphSection(this.dataset.sectionId, data.sections[this.dataset.sectionId]);
      console.log('Discount removed successfully');
    } catch (error) {
      // Ignore abort errors (they're expected when cancelling previous requests)
      if (error.name !== 'AbortError') {
        console.error('Error removing discount from offer:', error);
      } else {
        console.log('Request aborted (expected)');
      }
    } finally {
      this.#activeFetch = null;
    }
  };

  /**
   * Returns an array of existing discount codes.
   * @returns {string[]}
   */
  #existingDiscounts() {
    /** @type {string[]} */
    const discountCodes = [];

    // Get discount codes from pills (these are the applied codes shown in the list)
    // Include hidden pills too (for offer card compatibility)
    const discountPills = this.querySelectorAll('.cart-discount__pill');
    for (const pill of discountPills) {
      if (pill instanceof HTMLLIElement && typeof pill.dataset.discountCode === 'string') {
        const code = pill.dataset.discountCode.toUpperCase().trim();
        if (code && !discountCodes.includes(code)) {
          discountCodes.push(code);
        }
      }
    }

    // Also check for discount codes from offer card buttons (for offer card discounts)
    const offerRemoveButtons = this.querySelectorAll('.cart-discount__offer-remove-button[data-discount-code]');
    for (const button of offerRemoveButtons) {
      if (button instanceof HTMLButtonElement && typeof button.dataset.discountCode === 'string') {
        const code = button.dataset.discountCode.toUpperCase().trim();
        if (code && !discountCodes.includes(code)) {
          discountCodes.push(code);
        }
      }
    }

    // Also check applied clickable buttons (they have the cart-discount__clickable-button--applied class or are disabled)
    const appliedButtons = this.querySelectorAll('.cart-discount__clickable-button[data-coupon-code]');
    for (const button of appliedButtons) {
      if (button instanceof HTMLButtonElement && button.disabled && typeof button.dataset.couponCode === 'string') {
        const code = button.dataset.couponCode.toUpperCase().trim();
        if (code && !discountCodes.includes(code)) {
          discountCodes.push(code);
        }
      }
    }

    return discountCodes;
  }
}

if (!customElements.get('cart-discount-component')) {
  customElements.define('cart-discount-component', CartDiscount);
}
